<template>
  <!-- Inside the mount point on purpose: a teleport to body would fall outside the prefixed CSS. -->
  <div
    ref="overlay"
    class="fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto bg-black/40 px-4 pt-24 pb-10 sm:pt-32 sm:pb-[88px]"
    @click.self="close"
    @keydown.escape.prevent="close"
    @keydown.tab="trapFocus"
  >
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="estimation-title"
      class="relative flex w-full max-w-[619px] flex-col gap-6 rounded-xl bg-white p-6 sm:gap-8 sm:p-8"
    >
      <button
        ref="closeButton"
        type="button"
        aria-label="Sluiten"
        class="absolute top-5 right-5 flex size-11 cursor-pointer items-center justify-center rounded-full bg-surface-alt focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-bright"
        @click="close"
      >
        <CloseIcon class="size-3.5 text-heading" aria-hidden="true" />
      </button>

      <div class="flex flex-col gap-3">
        <Chip />
        <h3 id="estimation-title" class="text-[30px] leading-[1.15] font-bold text-heading">
          Help mijn verbruik <em class="text-orange italic">inschatten</em>
        </h3>
        <p class="text-base leading-6 font-normal text-body">
          Beantwoord twee vragen en we vullen een gemiddeld verbruik voor je in. Je kunt de waarden daarna altijd nog
          zelf aanpassen.
        </p>
      </div>

      <!-- The estimate could not be fetched; the visitor fills in the values manually instead. -->
      <p v-if="failed" role="alert" class="rounded-lg bg-surface-alt p-5 text-base leading-6 font-medium text-body">
        De schatting is nu niet beschikbaar. Vul je verbruik zelf in; je vindt het op je jaarafrekening.
      </p>

      <template v-else>
        <PersonPicker
          id="estimation-persons"
          v-model="persons"
          label="Aantal personen per huishouden"
          :options="ESTIMATE_PERSONS"
        />

        <StepperField
          id="estimation-panels"
          v-model="panels"
          label="Aantal zonnepanelen"
          :min="panelLimits.min"
          :max="panelLimits.max"
          decrease-label="Minder zonnepanelen"
          increase-label="Meer zonnepanelen"
        >
          <template #hint>
            <span v-if="estimate" class="text-base whitespace-nowrap text-muted">
              ± {{ formatNumber(estimate.generation) }} kWh opwek per jaar
            </span>
            <span v-else class="h-5 w-44 animate-pulse self-center rounded-md bg-line-soft" aria-hidden="true" />
          </template>
        </StepperField>

        <!-- The same fact the form asks, bound to the same store field: ticking it here or there
             is one and the same answer, so the two never disagree (#6331). -->
        <CheckboxField id="estimation-no-gas" v-model="noGasConnection">Ik heb geen gasaansluiting</CheckboxField>

        <div class="flex flex-col gap-5 rounded-lg bg-surface-alt p-5">
          <p class="text-base leading-6 font-normal text-body">Geschat verbruik en teruglevering</p>
          <!-- polite: the numbers follow every click, a screen reader hears the fresh estimate. -->
          <div v-if="estimate" class="flex flex-wrap items-center gap-5" aria-live="polite">
            <span class="flex items-center gap-2">
              <img :src="lightning" alt="Stroom afname" class="size-5" />
              <span class="text-[18px] font-medium text-heading">{{ formatNumber(estimate.electricity) }} kWh</span>
            </span>
            <span class="flex items-center gap-2">
              <img :src="sunPanel" alt="Stroom terugleveren" class="size-5" />
              <span class="text-[18px] font-medium text-heading">{{ formatNumber(estimate.generation) }} kWh</span>
            </span>
            <span v-if="input.hasGasConnection" class="flex items-center gap-2">
              <img :src="gasFlame" alt="Gasverbruik" class="size-5" />
              <span class="text-[18px] font-medium text-heading">{{ formatNumber(estimate.gas) }} m³</span>
            </span>
          </div>
          <!-- The averages are still on their way; same shape, so nothing jumps when they land. -->
          <div v-else class="flex flex-wrap items-center gap-5" aria-hidden="true">
            <span class="h-[27px] w-28 animate-pulse rounded-md bg-line-soft" />
            <span class="h-[27px] w-28 animate-pulse rounded-md bg-line-soft" />
            <span v-if="input.hasGasConnection" class="h-[27px] w-24 animate-pulse rounded-md bg-line-soft" />
          </div>
        </div>

        <Button :disabled="!estimate" @click="apply">Gebruik deze schatting</Button>
      </template>

      <p class="text-[14px] leading-[1.5] font-normal text-muted">
        Schatting op basis van landelijke gemiddelden (Milieu Centraal). Je kunt de ingevulde gegevens daarna zelf nog
        aanpassen.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'

import gasFlame from '@/assets/svg/gas-flame.svg'
import lightning from '@/assets/svg/lightning.svg'
import CloseIcon from '@/assets/svg/close.svg?component'
import sunPanel from '@/assets/svg/sun-panel.svg'
import Button from '@/components/Button.vue'
import CheckboxField from '@/components/form/CheckboxField.vue'
import Chip from '@/components/Chip.vue'
import PersonPicker from '@/components/form/PersonPicker.vue'
import StepperField from '@/components/form/StepperField.vue'
import { type Estimator, loadEstimator } from '@/services/estimation'
import { useInputStore } from '@/stores/input'
import { ESTIMATE_PERSONS } from '@/types/estimation'
import { formatNumber } from '@/utils/number'

const emit = defineEmits<{ close: []; applied: [] }>()

const input = useInputStore()

const estimator = ref<Estimator | null>(null)
const failed = ref(false)

// The store keeps the last answers, so a reopened modal continues where the visitor left off.
const { estimationPersons: persons, estimationPanels: panels } = storeToRefs(input)

const estimate = computed(() => estimator.value?.estimate(persons.value, panels.value) ?? null)

// The same flip the form makes: the checkbox asks "heb ik geen gas?", the store keeps the fact.
const noGasConnection = computed({
  get: () => !input.hasGasConnection,
  set: (value) => (input.hasGasConnection = !value),
})

// Until the averages land, the counter holds still on its default.
const panelLimits = computed(() => estimator.value?.panelLimits ?? { min: panels.value, max: panels.value })

const overlay = useTemplateRef('overlay')
const closeButton = useTemplateRef('closeButton')

onMounted(async () => {
  lockPageScroll()

  // preventScroll: the overlay is fixed, but focusing it would still scroll the page to the
  // form's document position, yanking the visitor to the top of the page.
  closeButton.value?.focus({ preventScroll: true })

  try {
    estimator.value = await loadEstimator()

    // The stored count is an assumption; the server bounds are the truth. Clamp before first render.
    const { min, max } = estimator.value.panelLimits
    panels.value = Math.min(Math.max(panels.value, min), max)
  } catch {
    // Without the averages the modal has nothing to offer; the message points back to the fields.
    failed.value = true
  }
})

onBeforeUnmount(unlockPageScroll)

let pageOverflow = ''
let pagePaddingRight = ''

/**
 * The page behind the dialog holds still while it is open; the overlay itself keeps scrolling for
 * modals taller than the screen. The padding replaces the scrollbar that disappears, so the page
 * content does not shift sideways.
 *
 * On the body, not the root element: the theme scrolls the page through the body's overflow, and
 * hiding the root's overflow would cut that propagation and snap the page back to the top.
 */
function lockPageScroll() {
  const scrollbar = window.innerWidth - document.documentElement.clientWidth

  pageOverflow = document.body.style.overflow
  pagePaddingRight = document.body.style.paddingRight
  document.body.style.overflow = 'hidden'

  if (scrollbar > 0) {
    document.body.style.paddingRight = `${scrollbar}px`
  }
}

function unlockPageScroll() {
  document.body.style.overflow = pageOverflow
  document.body.style.paddingRight = pagePaddingRight
}

function apply() {
  if (!estimate.value) {
    return
  }

  input.electricityUsage = String(estimate.value.electricity)
  input.generation = String(estimate.value.generation)

  // Without a gas connection there is no gas estimate to hand over; the field stays empty and grey.
  if (input.hasGasConnection) {
    input.gasUsage = String(estimate.value.gas)
  }

  emit('applied')
  close()
}

function close() {
  emit('close')
}

/** Keeps Tab inside the dialog; the page behind it is inert for the keyboard. */
function trapFocus(event: KeyboardEvent) {
  const focusable = overlay.value?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), output')

  if (!focusable?.length) {
    return
  }

  const first = focusable[0]
  const last = focusable[focusable.length - 1]

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}
</script>
